import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Sparkles, Check, ShieldCheck, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { getTeaser, submitDetails } from "@/lib/api";

const STATUS = ["Angestellt", "Arbeitslos (ALG I)", "Bürgergeld", "Selbstständig", "Rentner"];
const BILDUNG = ["Abgeschlossenes Studium (Uni/FH)", "Meister / Techniker", "Ausbildung", "Sonstiges"];
const ZIELGRUPPE = ["Privatkunden (B2C)", "Geschäftskunden (B2B)", "Behörden / Staat (B2G)"];
const MONET = ["Verkauf von Produkten", "Dienstleistung", "Vermittlung / Provision", "Sonstiges"];
const RADIUS = ["Lokal / Regional", "Deutschlandweit (online)", "Weltweit"];
const ENGPASS = [
  "Zu wenig Neukunden",
  "Umsatz & Wachstum",
  "Kosten & Rentabilität",
  "Kapital & Liquidität",
  "Personal & Fachkräfte",
  "Marketing & Sichtbarkeit",
  "Prozesse & Zeitmangel",
  "Bürokratie & Recht",
];
const ENGPASS_BIZ = [
  "Zu wenig Neukunden",
  "Zu geringe Conversion (zu viele Interessenten, zu wenig Abschlüsse)",
  "Fehlende Mitarbeiter",
  "Zeitmangel / Überlastung",
  "Kapitalmangel",
  "Bürokratie & Recht",
  "Unklare Positionierung",
  "Prozesse & Digitalisierung",
  "Sonstiges (Freitext)",
];
const TEAM = ["Solo-Gründer", "Team (2 oder mehr Personen)"];
const STATUS_BIZ = ["Selbstständig (Haupterwerb)", "Selbstständig (Nebenerwerb)", "Gewerbetreibend", "Freiberufler"];
const RECHTSFORM = ["Gewerbetreibend", "Freiberufler"];
const ERWERB = ["Haupterwerb", "Nebenerwerb"];

const mitarbeiterLabel = (n) => {
  const v = String(n ?? "").trim();
  if (v === "") return "";
  return v === "0" ? "Ich arbeite allein (0 Mitarbeiter)" : `${v} Mitarbeiter`;
};

export default function AnalyseForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [isLaeuft, setIsLaeuft] = useState(false);
  const [isBusiness, setIsBusiness] = useState(false);
  const [plzCities, setPlzCities] = useState([]);
  const [plzLoading, setPlzLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    idee: "",
    plz: "", ort: "", status: "", rechtsform: "", erwerb: "", bildung: "", bildung_detail: "", kapital: "",
    zielgruppe: [], zielgruppe_detail: "", monetarisierung: [], erloesmodell_detail: "",
    radius: "", radius_km: "", engpass: "", engpass_detail: "", team: "", mitarbeiterzahl: "",
    laufzeit_monate: "", monatsumsatz: "", anzahl_kunden: "", kunden_bezug: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const t = await getTeaser(id);
        if (!alive) return;
        if (!t.paid) { toast.warning("Bitte zuerst freischalten."); nav(`/teaser/${id}`, { replace: true }); return; }
        if (t.details_completed) { nav(`/report/${id}`, { replace: true }); return; }
        setIsLaeuft(t.business_status === "laeuft");
        setIsBusiness(t.audience === "business");
        setF((s) => ({ ...s, idee: t.idee || "" }));
      } catch (e) {
        if (e?.response?.status === 404) { toast.error("Bericht nicht gefunden. Bitte starte neu."); nav("/", { replace: true }); return; }
        toast.error("Konnte nicht geladen werden. Bitte Seite neu laden.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]); // eslint-disable-line

  // PLZ -> Städte-Vorschläge (Gründer & Business, gleiches Feld). Quelle: zippopotam.us (kein Key).
  useEffect(() => {
    const plz = f.plz.trim();
    if (!/^\d{5}$/.test(plz)) { setPlzCities([]); return; }
    let alive = true;
    setPlzLoading(true);
    fetch(`https://api.zippopotam.us/de/${plz}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        const names = (d?.places || []).map((p) => p["place name"]).filter(Boolean);
        const uniq = [...new Set(names)];
        setPlzCities(uniq);
        if (uniq.length === 1) set("ort", uniq[0]);
      })
      .catch(() => { if (alive) setPlzCities([]); })
      .finally(() => { if (alive) setPlzLoading(false); });
    return () => { alive = false; };
  }, [f.plz]); // eslint-disable-line

  const steps = useMemo(() => {
    const biz = isBusiness;
    const s = [
      { key: "idee", type: "idea", title: biz ? "Dein Business & Angebot" : "Deine Geschäftsidee", sub: biz ? "Du kannst dein Angebot hier noch schärfen oder ergänzen, bevor wir deinen Report erstellen." : "Du kannst deine Idee hier noch verfeinern oder ergänzen, bevor wir deinen Report erstellen." },
      { key: "standort", type: "location", title: biz ? "Wo ist dein Unternehmen ansässig?" : "Wo gründest du?", sub: "Damit wir dein Bundesland & passende Förderprogramme finden." },
      { key: "radius", type: "single", title: "In welchem Radius bist du aktiv?", options: RADIUS },
      ...(biz
        ? [
            { key: "rechtsform", type: "single", title: "Wie ist dein Unternehmen angemeldet?", options: RECHTSFORM },
            { key: "erwerb", type: "single", title: "Führst du es im Haupt- oder Nebenerwerb?", options: ERWERB },
          ]
        : [{ key: "status", type: "single", title: "Dein aktueller beruflicher Status?", options: STATUS }]),
      { key: "bildung", type: "single", title: "Dein höchster Abschluss?", options: BILDUNG },
      { key: "kapital", type: "capital", title: biz ? "Wie viel Kapital steht für Wachstum/Investitionen bereit?" : "Wie viel Eigenkapital steht bereit?", sub: "Trag einfach den Betrag in Euro ein – ein ungefährer Wert genügt." },
      { key: "zielgruppe", type: "multi", title: "Wer ist deine Zielgruppe?", sub: "Mehrfachauswahl möglich.", options: ZIELGRUPPE, detail: "zielgruppe_detail", detailPh: "Zielgruppe genauer beschreiben (optional)" },
      { key: "monetarisierung", type: "multi", title: "Wie verdient deine Unternehmung Geld?", sub: "Mehrfachauswahl möglich.", options: MONET, detail: "erloesmodell_detail", detailPh: "Erlösmodell genauer beschreiben (optional)" },
      { key: "engpass", type: "single", title: biz ? "Wo drückt aktuell der Schuh am meisten?" : "Deine größte Herausforderung?", options: biz ? ENGPASS_BIZ : ENGPASS, detail: "engpass_detail", detailPh: biz ? "Beschreibe deine konkrete Herausforderung (optional, aber hilfreich)" : "Was genau macht dir dabei aktuell Probleme? Optional – je konkreter deine Antwort, desto genauer die Analyse." },
      biz
        ? { key: "mitarbeiter", type: "mitarbeiter", title: "Wie viele Mitarbeiter hast du?", sub: "0 = ich arbeite allein." }
        : { key: "team", type: "single", title: "Wie ist dein Team aufgestellt?", options: TEAM },
    ];
    if (isLaeuft) s.push({ key: "kennzahlen", type: "metrics", title: "Kennzahlen deines laufenden Business" });
    return s;
  }, [isLaeuft, isBusiness]);

  const total = steps.length;
  const cur = steps[step];

  const stepValid = () => {
    if (!cur) return false;
    switch (cur.type) {
      case "idea": return f.idee.trim().length >= 15;
      case "location": return /^\d{5}$/.test(f.plz.trim()) && f.ort.trim().length >= 2;
      case "single": return !!f[cur.key];
      case "capital": return String(f.kapital).trim().length > 0;
      case "mitarbeiter": return String(f.mitarbeiterzahl).trim().length > 0;
      case "multi": return f[cur.key].length > 0;
      case "metrics": return f.laufzeit_monate && f.monatsumsatz && f.anzahl_kunden && (!isBusiness || f.kunden_bezug.trim().length > 0);
      default: return false;
    }
  };

  const goNext = () => {
    if (!stepValid()) { toast.warning("Bitte triff eine Auswahl, um fortzufahren."); return; }
    if (step < total - 1) setStep((s) => s + 1);
    else submit();
  };
  const goBack = () => step > 0 && setStep((s) => s - 1);

  const pickSingle = (key, val, autoAdvance) => {
    set(key, val);
    if (autoAdvance) setTimeout(() => setStep((s) => (s < total - 1 ? s + 1 : s)), 220);
  };

  const shouldAuto = (c, v) => {
    if (c.detail) return false;
    if (c.key === "radius") return v !== "Lokal / Regional"; // bei lokal: Radius-km eingeben lassen
    if (c.key === "bildung" && v === "Sonstiges") return false; // Freitext ermöglichen
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await submitDetails(id, {
        idee: f.idee.trim(),
        standort: `${f.plz} ${f.ort}`.trim(),
        status: isBusiness ? [f.erwerb, f.rechtsform].filter(Boolean).join(" · ") : f.status,
        rechtsform: f.rechtsform, erwerb: f.erwerb,
        bildung: f.bildung === "Sonstiges" && f.bildung_detail.trim() ? `Sonstiges: ${f.bildung_detail.trim()}` : f.bildung,
        kapital: f.kapital ? `${Number(f.kapital).toLocaleString("de-DE")} €` : "",
        zielgruppe: f.zielgruppe, zielgruppe_detail: f.zielgruppe_detail,
        monetarisierung: f.monetarisierung, erloesmodell_detail: f.erloesmodell_detail,
        radius: f.radius === "Lokal / Regional" && String(f.radius_km).trim() ? `Lokal / Regional (ca. ${f.radius_km} km)` : f.radius,
        engpass: f.engpass, engpass_detail: f.engpass_detail,
        team: isBusiness ? mitarbeiterLabel(f.mitarbeiterzahl) : f.team,
        mitarbeiterzahl: f.mitarbeiterzahl,
        laufzeit_monate: f.laufzeit_monate, monatsumsatz: f.monatsumsatz, anzahl_kunden: f.anzahl_kunden, kunden_bezug: f.kunden_bezug,
        email_opt_in: emailOptIn,
      });
      nav(`/report/${id}`);
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("Bericht nicht gefunden."); nav("/", { replace: true }); return; }
      toast.error(e?.response?.data?.detail || "Konnte nicht gesendet werden.");
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "#10C98D" }} /></div>;

  const pct = Math.round(((step + 1) / total) * 100);
  const isLast = step === total - 1;

  return (
    <div className={`min-h-screen bg-slate-50 ${isBusiness ? "theme-petrol" : ""}`}>
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-center"><Logo size="sm" variant={isBusiness ? "business" : "founder"} /></div>
      </header>

      <main className="max-w-xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {step === 0 && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-5 py-4 flex items-start gap-3 mb-6" data-testid="payment-success-banner">
            <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "var(--brand-solid)" }} />
            <p className="text-sm text-slate-700 leading-relaxed">
              <b>Zahlung erfolgreich – danke!</b> Damit wir dir das bestmögliche Ergebnis liefern,
              klären wir jetzt Schritt für Schritt noch ein paar Details. Danach {isBusiness ? "erstellen wir deine Business-Auswertung & dein PDF" : "berechnen wir deinen Score & erstellen dein PDF"}.
            </p>
          </div>
        )}

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-2">
            <span data-testid="step-indicator">Schritt {step + 1} von {total}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full rounded-full brand-gradient-bg transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div key={cur.key} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6 sm:p-8 animate-fade-in-up" data-testid="analyse-form">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{cur.title}</h1>
          {cur.sub && <p className="text-sm text-slate-500 mt-2">{cur.sub}</p>}

          <div className="mt-6">
            {cur.type === "idea" && (
              <>
                <Textarea data-testid="af-idee" value={f.idee} onChange={(e) => set("idee", e.target.value)} className="min-h-[160px] rounded-xl text-[15px]" placeholder="Beschreibe deine Geschäftsidee möglichst konkret …" autoFocus />
                <p className="text-xs text-slate-400 mt-2">{f.idee.trim().length} Zeichen · mind. 15</p>
              </>
            )}

            {cur.type === "mitarbeiter" && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Anzahl Mitarbeiter *</Label>
                <Input
                  data-testid="af-mitarbeiterzahl"
                  value={f.mitarbeiterzahl}
                  onChange={(e) => set("mitarbeiterzahl", e.target.value.replace(/\D/g, ""))}
                  inputMode="numeric"
                  className="mt-1.5 h-14 rounded-xl text-lg"
                  placeholder="z. B. 3"
                  autoFocus
                />
                <p className="text-xs text-slate-400 mt-2">0 = ich arbeite allein.</p>
              </div>
            )}

            {cur.type === "capital" && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Betrag in Euro *</Label>
                <div className="relative mt-1.5">
                  <Input
                    data-testid="af-kapital"
                    value={f.kapital ? Number(f.kapital).toLocaleString("de-DE") : ""}
                    onChange={(e) => set("kapital", e.target.value.replace(/\D/g, ""))}
                    inputMode="numeric"
                    className="h-14 rounded-xl text-lg pr-10"
                    placeholder="z. B. 15.000"
                    autoFocus
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">€</span>
                </div>
                <p className="text-xs text-slate-400 mt-2">Gib deinen ungefähren Betrag ein – z. B. Erspartes, geplantes Investment oder Kapital für Wachstum.</p>
              </div>
            )}

            {cur.type === "location" && (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">PLZ *</Label>
                  <Input data-testid="af-plz" value={f.plz} onChange={(e) => { set("plz", e.target.value.replace(/\D/g, "")); set("ort", ""); }} maxLength={5} inputMode="numeric" className="mt-1.5 h-12 rounded-xl" placeholder="10115" autoFocus />
                </div>
                {plzLoading && <p className="text-xs text-slate-400" data-testid="plz-loading">Passende Städte werden gesucht…</p>}
                {plzCities.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Ort wählen *</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-1.5">
                      {plzCities.map((c) => {
                        const active = f.ort === c;
                        return (
                          <button key={c} type="button" data-testid={`af-ort-${c}`} onClick={() => set("ort", c)}
                            className="text-left p-3.5 rounded-xl border-2 transition-all flex items-center justify-between gap-3"
                            style={{ borderColor: active ? "var(--brand-solid)" : "rgba(15,23,42,0.08)", background: active ? "var(--brand-tint)" : "#fff" }}>
                            <span className={`text-[15px] ${active ? "text-slate-900 font-semibold" : "text-slate-700"}`}>{c}</span>
                            {active && <Check className="h-4 w-4" style={{ color: "var(--brand-solid)" }} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {!plzLoading && plzCities.length === 0 && /^\d{5}$/.test(f.plz) && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Ort *</Label>
                    <Input data-testid="af-ort" value={f.ort} onChange={(e) => set("ort", e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="Berlin" />
                    <p className="text-xs text-slate-400 mt-1.5">Zu dieser PLZ konnten wir keinen Ort automatisch finden – bitte manuell eintragen.</p>
                  </div>
                )}
              </div>
            )}

            {cur.type === "single" && (
              <>
                <Choices options={cur.options} val={f[cur.key]} onPick={(v) => pickSingle(cur.key, v, shouldAuto(cur, v))} prefix={`af-${cur.key}`} cols={cur.options.length > 2 ? 2 : 2} />
                {cur.key === "radius" && f.radius === "Lokal / Regional" && (
                  <div className="mt-4">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Aktionsradius</Label>
                    <Input data-testid="af-radius-km" value={f.radius_km} onChange={(e) => set("radius_km", e.target.value.replace(/\D/g, ""))} inputMode="numeric" className="mt-1.5 h-12 rounded-xl" placeholder="Radius in km" />
                  </div>
                )}
                {cur.key === "bildung" && f.bildung === "Sonstiges" && (
                  <Textarea data-testid="af-bildung-detail" className="mt-4 min-h-[70px] rounded-xl" placeholder="Welcher Abschluss? (bitte angeben)" value={f.bildung_detail} onChange={(e) => set("bildung_detail", e.target.value)} />
                )}
                {cur.detail && (
                  <Textarea data-testid={`af-${cur.key}-detail`} className="mt-4 min-h-[90px] rounded-xl" placeholder={cur.detailPh} value={f[cur.detail]} onChange={(e) => set(cur.detail, e.target.value)} />
                )}
              </>
            )}

            {cur.type === "multi" && (
              <>
                <Choices options={cur.options} val={f[cur.key]} onPick={(v) => toggle(cur.key, v)} prefix={`af-${cur.key}`} cols={cur.options.length > 2 ? 2 : 1} multi />
                {cur.detail && (
                  <Textarea data-testid={`af-${cur.key}-detail`} className="mt-4 min-h-[90px] rounded-xl" placeholder={cur.detailPh} value={f[cur.detail]} onChange={(e) => set(cur.detail, e.target.value)} />
                )}
              </>
            )}

            {cur.type === "metrics" && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs uppercase tracking-widest text-slate-500">Monate aktiv *</Label><Input data-testid="af-laufzeit" value={f.laufzeit_monate} onChange={(e) => set("laufzeit_monate", e.target.value.replace(/\D/g, ""))} inputMode="numeric" className="mt-1.5 h-12 rounded-xl" placeholder="14" /></div>
                  <div><Label className="text-xs uppercase tracking-widest text-slate-500">Umsatz/Mon. € *</Label><Input data-testid="af-umsatz" value={f.monatsumsatz} onChange={(e) => set("monatsumsatz", e.target.value.replace(/[^\d.]/g, ""))} inputMode="numeric" className="mt-1.5 h-12 rounded-xl" placeholder="8000" /></div>
                  <div><Label className="text-xs uppercase tracking-widest text-slate-500">Kunden *</Label><Input data-testid="af-kunden" value={f.anzahl_kunden} onChange={(e) => set("anzahl_kunden", e.target.value.replace(/\D/g, ""))} inputMode="numeric" className="mt-1.5 h-12 rounded-xl" placeholder="12" /></div>
                </div>
                {isBusiness && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Worauf bezieht sich diese Zahl? *</Label>
                    <Input
                      data-testid="af-kunden-bezug"
                      value={f.kunden_bezug}
                      onChange={(e) => set("kunden_bezug", e.target.value)}
                      className="mt-1.5 h-12 rounded-xl"
                      placeholder="z. B. monatlich Neukunden, täglich Laufkundschaft, jährlich oder Anzahl Bestandskunden"
                    />
                    <p className="text-xs text-slate-400 mt-2">Damit wir deine Kundenzahl richtig einordnen (z. B. monatlich Neukunden, täglich Laufkundschaft, jährlich oder Anzahl Bestandskunden).</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {isLast && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="email-optin-card">
              <p className="text-sm font-semibold text-slate-900">Report zusätzlich per E-Mail erhalten</p>
              <p className="text-xs text-slate-500 leading-relaxed mt-1.5">
                Wenn du diese Option aktivierst, wird dein Report zur Zustellung vorübergehend über unseren
                E-Mail-Dienstleister verarbeitet. Gründio speichert deine Geschäftsidee und den vollständigen
                Report <b>nicht dauerhaft</b> in der eigenen Datenbank.
              </p>
              <label className="mt-3 flex items-start gap-3 cursor-pointer select-none" data-testid="email-optin-label">
                <input
                  type="checkbox"
                  data-testid="email-optin-checkbox"
                  checked={emailOptIn}
                  onChange={(e) => setEmailOptIn(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-slate-300 accent-[var(--brand-solid)] cursor-pointer"
                />
                <span className="text-sm text-slate-700">Ja, ich möchte meinen Report zusätzlich per E-Mail erhalten.</span>
              </label>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            <Button variant="ghost" onClick={goBack} disabled={step === 0} data-testid="af-back" className="text-slate-600">
              <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
            </Button>
            <Button onClick={goNext} disabled={submitting} data-testid={isLast ? "af-submit" : "af-next"} className="h-12 px-6 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95">
              {submitting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Wird gesendet…</>
                : isLast ? <><Sparkles className="h-5 w-5 mr-2" /> {isBusiness ? "Gründio Report erstellen" : "Score & Report erstellen"}</>
                : <>Weiter <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Choices({ options, val, onPick, prefix, cols = 1, multi = false }) {
  const arr = Array.isArray(val) ? val : [val];
  const grid = cols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1";
  return (
    <div className={`grid grid-cols-1 ${grid} gap-2.5`}>
      {options.map((o) => {
        const active = arr.includes(o);
        return (
          <button key={o} type="button" data-testid={`${prefix}-${o}`} onClick={() => onPick(o)}
            className="text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between gap-3"
            style={{ borderColor: active ? "var(--brand-solid)" : "rgba(15,23,42,0.08)", background: active ? "var(--brand-tint)" : "#fff" }}>
            <span className={`text-[15px] ${active ? "text-slate-900 font-semibold" : "text-slate-700"}`}>{o}</span>
            <span className="h-5 w-5 rounded-full flex items-center justify-center shrink-0" style={{ background: active ? "linear-gradient(135deg,var(--brand-g1),var(--brand-g3))" : "transparent", border: active ? "none" : "2px solid rgba(15,23,42,0.18)" }}>
              {active && <Check className="h-3 w-3 text-white" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}
